import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Password Strength Meter Component
 *
 * Displays real-time password strength feedback based on NIST 800-63B guidelines
 * and custom password validation rules.
 *
 * Features:
 * - Visual strength indicator (0-100%)
 * - Color-coded strength levels (weak/fair/good/strong)
 * - Compact inline requirement indicators
 * - Fixed height to prevent form jumping
 */
const PasswordStrengthMeter = ({ password, email = '', fullName = '', showRequirements = true }) => {
  const strength = useMemo(() => {
    if (!password) {
      return {
        score: 0,
        percentage: 0,
        level: 'none',
        color: 'gray',
        label: '',
        checks: {
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
          noEmail: true,
          noName: true,
          notCommon: true
        }
      };
    }

    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      noEmail: email ? !password.toLowerCase().includes(email.toLowerCase().split('@')[0]) : true,
      noName: fullName ? !password.toLowerCase().includes(fullName.toLowerCase().split(' ')[0]) : true,
      notCommon: !isCommonPassword(password)
    };

    // Calculate score based on checks
    let score = 0;
    if (checks.length) score += 25;
    if (checks.uppercase && checks.lowercase) score += 15;
    if (checks.number) score += 15;
    if (checks.special) score += 20;
    if (checks.noEmail) score += 10;
    if (checks.noName) score += 10;
    if (checks.notCommon) score += 5;

    // Determine strength level
    let level, color, label;
    if (score < 40) {
      level = 'weak';
      color = 'red';
      label = 'Weak';
    } else if (score < 60) {
      level = 'fair';
      color = 'orange';
      label = 'Fair';
    } else if (score < 80) {
      level = 'good';
      color = 'yellow';
      label = 'Good';
    } else {
      level = 'strong';
      color = 'green';
      label = 'Strong';
    }

    return {
      score,
      percentage: score,
      level,
      color,
      label,
      checks
    };
  }, [password, email, fullName]);

  const getBarColor = (color) => {
    const colors = {
      gray: 'bg-gray-300',
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500'
    };
    return colors[color] || colors.gray;
  };

  const getTextColor = (color) => {
    const colors = {
      gray: 'text-gray-500',
      red: 'text-red-600',
      orange: 'text-orange-600',
      yellow: 'text-yellow-600',
      green: 'text-green-600'
    };
    return colors[color] || colors.gray;
  };

  // Count how many requirements are met
  const metCount = Object.values(strength.checks).filter(Boolean).length;
  const totalCount = Object.keys(strength.checks).length;

  return (
    <div className="space-y-2">
      {/* Compact Strength Bar with Label */}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getBarColor(strength.color)}`}
            style={{ width: `${strength.percentage}%` }}
          />
        </div>
        <span className={`text-xs font-medium min-w-[45px] text-right ${getTextColor(strength.color)}`}>
          {strength.label || '—'}
        </span>
      </div>

      {/* Compact Inline Requirements - Always visible, fixed height */}
      {showRequirements && (
        <div className="flex flex-wrap gap-1.5">
          <RequirementChip met={strength.checks.length} label="8+ chars" />
          <RequirementChip met={strength.checks.uppercase} label="A-Z" />
          <RequirementChip met={strength.checks.lowercase} label="a-z" />
          <RequirementChip met={strength.checks.number} label="0-9" />
          <RequirementChip met={strength.checks.special} label="!@#" />
          {!strength.checks.notCommon && password && (
            <RequirementChip met={false} label="Common" isWarning />
          )}
          {email && !strength.checks.noEmail && (
            <RequirementChip met={false} label="Has email" isWarning />
          )}
          {fullName && !strength.checks.noName && (
            <RequirementChip met={false} label="Has name" isWarning />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact requirement chip component
 */
const RequirementChip = ({ met, label, isWarning = false }) => {
  if (isWarning) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600 border border-red-200">
        <X className="w-2.5 h-2.5" />
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all duration-200 ${
        met
          ? 'bg-green-100 text-green-700 border border-green-200'
          : 'bg-gray-100 text-gray-400 border border-gray-200'
      }`}
    >
      {met ? (
        <Check className="w-2.5 h-2.5" />
      ) : (
        <span className="w-2.5 h-2.5 flex items-center justify-center">○</span>
      )}
      {label}
    </span>
  );
};

/**
 * Check if password is in common passwords list
 */
const isCommonPassword = (password) => {
  const commonPasswords = [
    'password', 'password123', '12345678', 'qwerty', 'abc123',
    'monkey', '1234567', 'letmein', 'trustno1', 'dragon',
    'baseball', 'iloveyou', 'master', 'sunshine', 'ashley',
    'bailey', 'passw0rd', 'shadow', '123123', '654321',
    'superman', 'qazwsx', 'michael', 'football', 'welcome',
    'jesus', 'ninja', 'mustang', 'password1', '123456789'
  ];

  return commonPasswords.includes(password.toLowerCase());
};

export default PasswordStrengthMeter;
